import type { PassportSchemaDef } from "../types.js";

/**
 * DRAFT aligned to the Battery Pass consortium data model v1.2
 * (github.com/batterypass/BatteryPassDataModel, CC-BY-4.0; DIN DKE SPEC
 * 99100:2025-02) and Regulation (EU) 2023/1542 Art. 77 + Annex XIII, pending
 * the final implementing acts (legitimate-interest access rules expected
 * Q4 2026; the EV carbon-footprint methodology delegated act is still open).
 * Field ids are STABLE (persisted in passport versions); the list will be
 * trued up against the final acts by adding fields / flipping `required` and
 * `tier`, never by renaming ids.
 *
 * Scope: this is the STATIC model/batch template. Per-unit dynamic data —
 * state of health telemetry, state of charge, usage history (Annex XIII
 * part 4) — is intentionally out of scope; it belongs to a unit-level data
 * layer, not this schema. `performance` carries only the static reference
 * parameters needed to interpret such telemetry.
 *
 * Tier logic: unlike textile-v1, several REQUIRED fields are restricted —
 * Annex XIII part 2 content (dismantling, safety measures, detailed
 * composition) is mandatory for a compliant passport but accessible only to
 * legitimate-interest actors (repairers, remanufacturers, second-life
 * operators, recyclers). `required` governs publish-compliance, `tier`
 * governs visibility; the engines treat them independently. The regulation's
 * third layer (authorities/notified bodies only) is collapsed into
 * "restricted" until the platform grows finer-grained roles.
 */
export const batteryV1: PassportSchemaDef = {
  id: "battery-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Batarya Pasaportu",
    en: "Battery Passport",
    de: "Batteriepass",
  },
  // Draft vocabulary: schema.org for generic product terms, GS1 web vocabulary
  // for identifiers, own namespace for regulation-specific terms. The Battery
  // Pass consortium publishes generated JSON-LD contexts we can adopt once the
  // implementing acts fix an official one.
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/battery/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "general",
      labels: {
        tr: "Genel Ürün ve Üretici Bilgisi",
        en: "General Product & Manufacturer Information",
        de: "Allgemeine Produkt- & Herstellerinformationen",
      },
      fields: [
        {
          id: "general.batteryModel",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Batarya modeli", en: "Battery model", de: "Batteriemodell" },
        },
        {
          id: "general.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short consumer-facing description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "general.modelIdentifier",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Model tanımlayıcısı", en: "Model identifier", de: "Modellkennung" },
          description: "Manufacturer's model/part number complementing the passport identifier.",
        },
        {
          id: "general.gtin",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "general.batteryCategory",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["ev", "lmt", "industrial"],
          labels: { tr: "Batarya kategorisi", en: "Battery category", de: "Batteriekategorie" },
          description:
            "Passport-obliged categories: EV, light means of transport, industrial > 2 kWh (Art. 77).",
        },
        {
          id: "general.batteryChemistry",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: [
            "li-nmc",
            "li-nca",
            "li-lfp",
            "li-lmo",
            "li-other",
            "sodium-ion",
            "nimh",
            "lead-acid",
            "solid-state",
            "other",
          ],
          labels: { tr: "Batarya kimyası", en: "Battery chemistry", de: "Batteriechemie" },
          description: "Cell chemistry family — extend the list as new chemistries reach the market.",
        },
        {
          id: "general.batteryStatus",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["original", "repurposed", "reused", "remanufactured", "waste"],
          labels: { tr: "Batarya durumu", en: "Battery status", de: "Batteriestatus" },
          description:
            "Life-cycle status declared at placing on the market; a status change means a new passport version.",
        },
        {
          id: "general.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "general.manufacturerIdentifier",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Üretici tanımlayıcısı", en: "Manufacturer identifier", de: "Herstellerkennung" },
          description: "Legal-entity identifier (e.g. EORI) for customs/registry flows.",
        },
        {
          id: "general.operatorContactUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Ekonomik operatör iletişim bağlantısı",
            en: "Economic operator contact link",
            de: "Kontakt-Link des Wirtschaftsakteurs",
          },
        },
        {
          id: "general.importerName",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "İthalatçı adı", en: "Importer name", de: "Name des Importeurs" },
          description: "Responsible operator when the manufacturer is outside the EU.",
        },
        {
          id: "general.manufacturingDate",
          type: "date",
          required: true,
          tier: "public",
          labels: { tr: "Üretim tarihi", en: "Manufacturing date", de: "Herstellungsdatum" },
        },
        {
          id: "general.placedOnMarketDate",
          type: "date",
          required: false,
          tier: "public",
          labels: {
            tr: "Piyasaya arz tarihi",
            en: "Date of placing on the market",
            de: "Datum des Inverkehrbringens",
          },
        },
        {
          id: "general.manufacturingPlace",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretim yeri", en: "Place of manufacture", de: "Herstellungsort" },
          description: "Country (ISO 3166-1 alpha-2) and site of manufacture.",
        },
        {
          id: "general.manufacturingFacilityId",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Üretim tesisi kimliği",
            en: "Manufacturing facility id",
            de: "Kennung der Fertigungsstätte",
          },
          description: "Facility identifier such as a GS1 GLN.",
        },
        {
          id: "general.batteryWeightKg",
          type: "number",
          required: true,
          tier: "public",
          unit: "kg",
          labels: { tr: "Batarya ağırlığı", en: "Battery weight", de: "Batteriegewicht" },
        },
        {
          id: "general.nominalEnergyKwh",
          type: "number",
          required: true,
          tier: "public",
          unit: "kWh",
          labels: { tr: "Nominal enerji", en: "Nominal energy", de: "Nennenergie" },
          highlight: true,
        },
        {
          id: "general.nominalVoltageV",
          type: "number",
          required: true,
          tier: "public",
          unit: "V",
          labels: { tr: "Nominal gerilim", en: "Nominal voltage", de: "Nennspannung" },
        },
        {
          id: "general.minVoltageV",
          type: "number",
          required: false,
          tier: "public",
          unit: "V",
          labels: { tr: "Asgari gerilim", en: "Minimum voltage", de: "Mindestspannung" },
        },
        {
          id: "general.maxVoltageV",
          type: "number",
          required: false,
          tier: "public",
          unit: "V",
          labels: { tr: "Azami gerilim", en: "Maximum voltage", de: "Maximalspannung" },
        },
        {
          id: "general.warrantyPeriodMonths",
          type: "number",
          required: false,
          tier: "public",
          unit: "months",
          labels: { tr: "Garanti süresi", en: "Warranty period", de: "Garantiedauer" },
        },
        {
          id: "general.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Matched against the customs declaration via the EU Registry.",
        },
      ],
    },
    {
      id: "carbonFootprint",
      labels: { tr: "Karbon Ayak İzi", en: "Carbon Footprint", de: "CO₂-Fußabdruck" },
      fields: [
        {
          id: "carbonFootprint.totalKgCo2ePerKwh",
          type: "number",
          required: true,
          tier: "public",
          unit: "kg CO₂e/kWh",
          labels: {
            tr: "Karbon ayak izi (kWh başına)",
            en: "Carbon footprint per kWh",
            de: "CO₂-Fußabdruck pro kWh",
          },
          description:
            "Life-cycle kg CO₂e per kWh of total energy over service life (Art. 7) — the declaration " +
            "obligation phases in with the pending methodology delegated act.",
          highlight: true,
        },
        {
          id: "carbonFootprint.absoluteKgCo2e",
          type: "number",
          required: false,
          tier: "public",
          unit: "kg CO₂e",
          labels: {
            tr: "Toplam karbon ayak izi",
            en: "Total carbon footprint",
            de: "Gesamter CO₂-Fußabdruck",
          },
        },
        {
          id: "carbonFootprint.performanceClass",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["A", "B", "C", "D", "E", "F", "G"],
          labels: {
            tr: "Karbon ayak izi performans sınıfı",
            en: "Carbon footprint performance class",
            de: "CO₂-Leistungsklasse",
          },
          description: "Class boundaries pending the Art. 7 delegated act — A-G scale assumed.",
        },
        {
          id: "carbonFootprint.methodologyReference",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Metodoloji referansı", en: "Methodology reference", de: "Methodikreferenz" },
          description: "Calculation methodology / PEFCR version used for the declaration.",
        },
        {
          id: "carbonFootprint.studyUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Karbon ayak izi çalışması",
            en: "Carbon footprint study",
            de: "CO₂-Fußabdruck-Studie",
          },
          description: "Web link to the public version of the carbon footprint study (Annex XIII).",
        },
        {
          id: "carbonFootprint.rawMaterialSharePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Hammadde edinimi payı",
            en: "Raw material acquisition share",
            de: "Anteil Rohstoffgewinnung",
          },
          description: "Life-cycle stage share 1 of 4: raw material acquisition & pre-processing.",
        },
        {
          id: "carbonFootprint.manufacturingSharePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Üretim payı", en: "Manufacturing share", de: "Anteil Herstellung" },
          description: "Life-cycle stage share 2 of 4: main product production.",
        },
        {
          id: "carbonFootprint.distributionSharePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Dağıtım payı", en: "Distribution share", de: "Anteil Distribution" },
          description: "Life-cycle stage share 3 of 4: distribution.",
        },
        {
          id: "carbonFootprint.endOfLifeSharePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Ömür sonu ve geri dönüşüm payı",
            en: "End-of-life & recycling share",
            de: "Anteil Lebensende & Recycling",
          },
          description: "Life-cycle stage share 4 of 4: end-of-life & recycling.",
        },
        {
          id: "carbonFootprint.declarationId",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Karbon ayak izi beyan kimliği",
            en: "Carbon footprint declaration id",
            de: "Kennung der CO₂-Erklärung",
          },
        },
        {
          id: "carbonFootprint.verificationBody",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Doğrulama kuruluşu", en: "Verification body", de: "Prüfstelle" },
          description: "Third party that verified the carbon footprint declaration.",
        },
      ],
    },
    {
      id: "circularity",
      labels: { tr: "Döngüsellik", en: "Circularity", de: "Kreislauffähigkeit" },
      fields: [
        {
          id: "circularity.recycledCobaltPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülmüş kobalt payı",
            en: "Recycled cobalt share",
            de: "Rezyklatanteil Kobalt",
          },
          description: "Recovered-from-waste share in active materials (Art. 8) — obligation phases in.",
        },
        {
          id: "circularity.recycledLithiumPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülmüş lityum payı",
            en: "Recycled lithium share",
            de: "Rezyklatanteil Lithium",
          },
        },
        {
          id: "circularity.recycledNickelPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülmüş nikel payı",
            en: "Recycled nickel share",
            de: "Rezyklatanteil Nickel",
          },
        },
        {
          id: "circularity.recycledLeadPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülmüş kurşun payı",
            en: "Recycled lead share",
            de: "Rezyklatanteil Blei",
          },
        },
        {
          id: "circularity.renewableContentSharePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Yenilenebilir içerik payı",
            en: "Renewable content share",
            de: "Anteil erneuerbarer Materialien",
          },
        },
        {
          id: "circularity.sparePartsAvailable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Yedek parça mevcut", en: "Spare parts available", de: "Ersatzteile verfügbar" },
        },
        {
          id: "circularity.sparePartsInfo",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yedek parça bilgisi",
            en: "Spare parts information",
            de: "Ersatzteilinformationen",
          },
          description: "Cells, modules, BMS components and other replaceable parts.",
        },
        {
          id: "circularity.sparePartsSourcesUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Yedek parça tedarik kaynakları",
            en: "Spare part sources",
            de: "Bezugsquellen für Ersatzteile",
          },
        },
        {
          id: "circularity.dismantlingInfo",
          type: "string",
          required: true,
          tier: "restricted",
          labels: {
            tr: "Söküm ve demontaj bilgisi",
            en: "Dismantling & disassembly information",
            de: "Demontageinformationen",
          },
          description:
            "Mandatory Annex XIII part 2 content, gated to legitimate-interest actors " +
            "(repairers, remanufacturers, second-life operators, recyclers).",
        },
        {
          id: "circularity.dismantlingInfoUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Söküm kılavuzu bağlantısı",
            en: "Dismantling manual link",
            de: "Link zur Demontageanleitung",
          },
        },
        {
          id: "circularity.safetyMeasures",
          type: "string",
          required: true,
          tier: "restricted",
          labels: { tr: "Güvenlik önlemleri", en: "Safety measures", de: "Sicherheitsmaßnahmen" },
          description:
            "Safety requirements for handling, storage, testing and transport — Annex XIII part 2, " +
            "legitimate-interest access.",
        },
        {
          id: "circularity.extinguishingAgent",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Söndürücü madde", en: "Extinguishing agent", de: "Löschmittel" },
        },
        {
          id: "circularity.remanufacturingInfoUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Yeniden üretim bilgisi bağlantısı",
            en: "Remanufacturing information link",
            de: "Link zu Refabrikationsinformationen",
          },
        },
        {
          id: "circularity.secondLifeSuitability",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "İkinci yaşam uygunluğu",
            en: "Second-life suitability",
            de: "Eignung für Second-Life",
          },
          description: "Suitability for repurposing (e.g. stationary storage) after first use.",
        },
        {
          id: "circularity.wastePreventionInfo",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Atık önleme bilgisi",
            en: "Waste prevention information",
            de: "Informationen zur Abfallvermeidung",
          },
          description: "Role of end-users in preventing battery waste.",
        },
        {
          id: "circularity.separateCollectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ayrı toplama bilgisi",
            en: "Separate collection information",
            de: "Informationen zur getrennten Sammlung",
          },
          description: "Where/how to return the battery — collection and take-back obligations.",
        },
        {
          id: "circularity.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
      ],
    },
    {
      id: "materialComposition",
      labels: { tr: "Malzeme Bileşimi", en: "Material Composition", de: "Materialzusammensetzung" },
      fields: [
        {
          id: "materialComposition.compositionOverview",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Bileşim özeti", en: "Composition overview", de: "Zusammensetzungsübersicht" },
          description: "Overall material composition by weight share, consumer-level granularity.",
        },
        {
          id: "materialComposition.criticalRawMaterials",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Kritik hammaddeler", en: "Critical raw materials", de: "Kritische Rohstoffe" },
          description:
            "EU CRM-list materials present above 0.1% w/w (e.g. cobalt, lithium, natural graphite).",
        },
        {
          id: "materialComposition.criticalRawMaterialsLocation",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Kritik hammaddelerin konumu",
            en: "Location of critical raw materials",
            de: "Ort der kritischen Rohstoffe",
          },
          description: "Which components contain them — relevant for dismantlers and recyclers.",
        },
        {
          id: "materialComposition.cathodeActiveMaterials",
          type: "string",
          required: true,
          tier: "restricted",
          labels: {
            tr: "Katot aktif malzemeleri",
            en: "Cathode active materials",
            de: "Aktivmaterialien der Kathode",
          },
          description: "Detailed composition — Annex XIII part 2, legitimate-interest access.",
        },
        {
          id: "materialComposition.anodeActiveMaterials",
          type: "string",
          required: true,
          tier: "restricted",
          labels: {
            tr: "Anot aktif malzemeleri",
            en: "Anode active materials",
            de: "Aktivmaterialien der Anode",
          },
          description: "Detailed composition — Annex XIII part 2, legitimate-interest access.",
        },
        {
          id: "materialComposition.electrolyteComposition",
          type: "string",
          required: true,
          tier: "restricted",
          labels: {
            tr: "Elektrolit bileşimi",
            en: "Electrolyte composition",
            de: "Elektrolytzusammensetzung",
          },
          description: "Detailed composition — Annex XIII part 2, legitimate-interest access.",
        },
        {
          id: "materialComposition.hazardousSubstances",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Tehlikeli maddeler", en: "Hazardous substances", de: "Gefahrstoffe" },
          description: "Hazardous substances present other than mercury, cadmium or lead.",
        },
        {
          id: "materialComposition.hazardousSubstancesLocation",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tehlikeli maddelerin konumu",
            en: "Location of hazardous substances",
            de: "Ort der Gefahrstoffe",
          },
        },
        {
          id: "materialComposition.svhcPresent",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          description: "REACH candidate-list substances of very high concern above 0.1% by weight.",
        },
        {
          id: "materialComposition.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "SVHC madde listesi", en: "SVHC substances", de: "SVHC-Stoffe" },
          description: "Substance names/CAS numbers — expected when svhcPresent is true.",
        },
        {
          id: "materialComposition.mercuryAboveThreshold",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Cıva eşik üstünde (>%0,0005)",
            en: "Mercury above threshold (>0.0005%)",
            de: "Quecksilber über Schwellenwert (>0,0005 %)",
          },
          description: "Above threshold the Hg chemical symbol must also appear on the label.",
        },
        {
          id: "materialComposition.cadmiumAboveThreshold",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Kadmiyum eşik üstünde (>%0,002)",
            en: "Cadmium above threshold (>0.002%)",
            de: "Cadmium über Schwellenwert (>0,002 %)",
          },
        },
        {
          id: "materialComposition.leadAboveThreshold",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Kurşun eşik üstünde (>%0,004)",
            en: "Lead above threshold (>0.004%)",
            de: "Blei über Schwellenwert (>0,004 %)",
          },
        },
      ],
    },
    {
      id: "performance",
      labels: {
        tr: "Performans ve Dayanıklılık",
        en: "Performance & Durability",
        de: "Leistung & Haltbarkeit",
      },
      fields: [
        {
          id: "performance.ratedCapacityAh",
          type: "number",
          required: true,
          tier: "public",
          unit: "Ah",
          labels: { tr: "Anma kapasitesi", en: "Rated capacity", de: "Nennkapazität" },
          highlight: true,
        },
        {
          id: "performance.capacityThresholdForExhaustionPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tükenme kapasite eşiği",
            en: "Capacity threshold for exhaustion",
            de: "Kapazitätsschwelle für Erschöpfung",
          },
          description: "Remaining-capacity share below which the battery counts as exhausted.",
        },
        {
          id: "performance.expectedCapacityFadePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Beklenen kapasite kaybı",
            en: "Expected capacity fade",
            de: "Erwarteter Kapazitätsverlust",
          },
          description: "Expected fade over the reference service life/cycle-life test.",
        },
        {
          id: "performance.cycleLife",
          type: "number",
          required: true,
          tier: "public",
          unit: "cycles",
          labels: {
            tr: "Beklenen çevrim ömrü",
            en: "Expected cycle life",
            de: "Erwartete Zyklenlebensdauer",
          },
          description: "Expected number of full charge-discharge cycles under the reference test.",
          highlight: true,
        },
        {
          id: "performance.cycleLifeTestReference",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Çevrim ömrü test referansı",
            en: "Cycle-life test reference",
            de: "Referenz des Zyklentests",
          },
          description: "Standard and conditions the cycle-life claim is based on.",
        },
        {
          id: "performance.depthOfDischargeInCycleLifePct",
          type: "percentage",
          required: false,
          tier: "restricted",
          unit: "%",
          labels: {
            tr: "Çevrim testinde deşarj derinliği",
            en: "Depth of discharge in cycle-life test",
            de: "Entladetiefe im Zyklentest",
          },
        },
        {
          id: "performance.expectedLifetimeYears",
          type: "number",
          required: true,
          tier: "public",
          unit: "years",
          labels: { tr: "Beklenen kullanım ömrü", en: "Expected lifetime", de: "Erwartete Lebensdauer" },
          description: "Expected lifetime under the reference conditions of intended use.",
          highlight: true,
        },
        {
          id: "performance.nominalPowerW",
          type: "number",
          required: true,
          tier: "public",
          unit: "W",
          labels: { tr: "Nominal güç", en: "Nominal power", de: "Nennleistung" },
        },
        {
          id: "performance.powerCapabilityAt20PctSocW",
          type: "number",
          required: false,
          tier: "public",
          unit: "W",
          labels: {
            tr: "%20 şarj durumunda güç kapasitesi",
            en: "Power capability at 20% state of charge",
            de: "Leistungsfähigkeit bei 20 % Ladezustand",
          },
        },
        {
          id: "performance.powerFadePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Beklenen güç kaybı", en: "Expected power fade", de: "Erwarteter Leistungsverlust" },
        },
        {
          id: "performance.maxChargeRateC",
          type: "number",
          required: false,
          tier: "public",
          unit: "C",
          labels: { tr: "Azami şarj hızı", en: "Maximum charge rate", de: "Maximale Laderate" },
        },
        {
          id: "performance.maxDischargeRateC",
          type: "number",
          required: false,
          tier: "public",
          unit: "C",
          labels: { tr: "Azami deşarj hızı", en: "Maximum discharge rate", de: "Maximale Entladerate" },
        },
        {
          id: "performance.roundTripEfficiencyPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Gidiş-dönüş verimliliği",
            en: "Round-trip efficiency",
            de: "Round-Trip-Wirkungsgrad",
          },
          description: "Energy round-trip efficiency at beginning of life.",
        },
        {
          id: "performance.roundTripEfficiencyAt50PctCycleLifePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Çevrim ömrünün %50'sinde gidiş-dönüş verimliliği",
            en: "Round-trip efficiency at 50% of cycle life",
            de: "Round-Trip-Wirkungsgrad bei 50 % der Zyklenlebensdauer",
          },
        },
        {
          id: "performance.internalResistancePackMohm",
          type: "number",
          required: false,
          tier: "public",
          unit: "mΩ",
          labels: { tr: "Paket iç direnci", en: "Pack internal resistance", de: "Innenwiderstand des Packs" },
        },
        {
          id: "performance.internalResistanceCellMohm",
          type: "number",
          required: false,
          tier: "restricted",
          unit: "mΩ",
          labels: { tr: "Hücre iç direnci", en: "Cell internal resistance", de: "Innenwiderstand der Zelle" },
          description: "Cell-level detail — supplier/remanufacturer relevance.",
        },
        {
          id: "performance.internalResistanceIncreasePct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Beklenen iç direnç artışı",
            en: "Expected internal resistance increase",
            de: "Erwarteter Anstieg des Innenwiderstands",
          },
        },
        {
          id: "performance.selfDischargePctPerMonth",
          type: "number",
          required: false,
          tier: "public",
          unit: "%/month",
          labels: { tr: "Öz deşarj oranı", en: "Self-discharge rate", de: "Selbstentladungsrate" },
        },
        {
          id: "performance.operatingTempMinC",
          type: "number",
          required: true,
          tier: "public",
          unit: "°C",
          labels: {
            tr: "Asgari çalışma sıcaklığı",
            en: "Minimum operating temperature",
            de: "Minimale Betriebstemperatur",
          },
          description: "Temperature range the battery can withstand (Annex IV part A).",
        },
        {
          id: "performance.operatingTempMaxC",
          type: "number",
          required: true,
          tier: "public",
          unit: "°C",
          labels: {
            tr: "Azami çalışma sıcaklığı",
            en: "Maximum operating temperature",
            de: "Maximale Betriebstemperatur",
          },
        },
        {
          id: "performance.energyDensityWhKg",
          type: "number",
          required: false,
          tier: "public",
          unit: "Wh/kg",
          labels: { tr: "Enerji yoğunluğu", en: "Energy density", de: "Energiedichte" },
        },
        {
          id: "performance.sohReferenceParameters",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "SoH referans parametreleri",
            en: "SoH reference parameters",
            de: "SoH-Referenzparameter",
          },
          description:
            "Static reference values needed to interpret per-unit state-of-health telemetry; the " +
            "telemetry itself is dynamic unit data and out of this schema's scope.",
        },
      ],
    },
    {
      id: "labels",
      labels: {
        tr: "Etiketler ve Sertifikasyon",
        en: "Labels & Certification",
        de: "Kennzeichnung & Zertifizierung",
      },
      fields: [
        {
          id: "labels.ceMarking",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "CE işareti", en: "CE marking", de: "CE-Kennzeichnung" },
        },
        {
          id: "labels.declarationOfConformityId",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "AB uygunluk beyanı kimliği",
            en: "EU declaration of conformity id",
            de: "Kennung der EU-Konformitätserklärung",
          },
        },
        {
          id: "labels.declarationOfConformityUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "AB uygunluk beyanı",
            en: "EU declaration of conformity",
            de: "EU-Konformitätserklärung",
          },
        },
        {
          id: "labels.separateCollectionSymbol",
          type: "boolean",
          required: true,
          tier: "public",
          labels: {
            tr: "Ayrı toplama sembolü",
            en: "Separate collection symbol",
            de: "Symbol zur getrennten Sammlung",
          },
          description: "Crossed-out wheelie bin present on the label.",
        },
        {
          id: "labels.chemicalSymbols",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Kimyasal semboller", en: "Chemical symbols", de: "Chemische Symbole" },
          description: "Cd/Pb symbols below the collection symbol when above thresholds.",
        },
        {
          id: "labels.labelsExplanationUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Etiket açıklamaları",
            en: "Explanation of labels",
            de: "Erläuterung der Kennzeichnungen",
          },
        },
        {
          id: "labels.certifications",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Sertifikalar", en: "Certifications", de: "Zertifizierungen" },
          description: 'Voluntary schemes and standards conformity, e.g. "IEC 62619; UN 38.3".',
        },
        {
          id: "labels.notifiedBodyId",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Onaylanmış kuruluş kimliği",
            en: "Notified body id",
            de: "Kennung der notifizierten Stelle",
          },
          description: "Notified body that performed the conformity assessment.",
        },
        {
          id: "labels.testReportsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: { tr: "Test raporları", en: "Test reports", de: "Prüfberichte" },
          description:
            "Detailed test results — Annex XIII reserves these for authorities; collapsed into the " +
            "restricted tier until finer-grained roles exist.",
        },
      ],
    },
    {
      id: "dueDiligence",
      labels: {
        tr: "Tedarik Zinciri Durum Tespiti",
        en: "Supply-Chain Due Diligence",
        de: "Sorgfaltspflichten in der Lieferkette",
      },
      fields: [
        {
          id: "dueDiligence.reportUrl",
          type: "url",
          required: true,
          tier: "public",
          labels: { tr: "Durum tespiti raporu", en: "Due diligence report", de: "Sorgfaltspflichtenbericht" },
          description:
            "Publicly available battery due diligence policy report (Art. 52) — obligation applies at " +
            "the latest from August 2028.",
        },
        {
          id: "dueDiligence.policySummary",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Durum tespiti politikası özeti",
            en: "Due diligence policy summary",
            de: "Zusammenfassung der Sorgfaltspflichten-Strategie",
          },
        },
        {
          id: "dueDiligence.thirdPartyVerification",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Üçüncü taraf doğrulaması",
            en: "Third-party verification",
            de: "Verifizierung durch Dritte",
          },
          description: "Whether the due diligence policy was verified by a notified body.",
        },
        {
          id: "dueDiligence.verificationBody",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Doğrulama kuruluşu", en: "Verification body", de: "Prüfstelle" },
        },
        {
          id: "dueDiligence.oecdAlignment",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "OECD rehberiyle uyumlu",
            en: "Aligned with OECD guidance",
            de: "Im Einklang mit OECD-Leitlinien",
          },
          description: "OECD Due Diligence Guidance for Responsible Supply Chains of Minerals.",
        },
        {
          id: "dueDiligence.grievanceMechanismUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Şikâyet mekanizması", en: "Grievance mechanism", de: "Beschwerdemechanismus" },
        },
        {
          id: "dueDiligence.rawMaterialOrigins",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Hammadde menşe ülkeleri",
            en: "Raw material countries of origin",
            de: "Herkunftsländer der Rohstoffe",
          },
          description: "Sourcing countries for cobalt, lithium, nickel, natural graphite.",
        },
        {
          id: "dueDiligence.supplyChainIndices",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Tedarik zinciri endeksleri",
            en: "Supply chain indices",
            de: "Lieferketten-Indizes",
          },
          description: "Battery Pass supply-chain transparency indices — definition pending.",
        },
      ],
    },
  ],
};
